import { useReducer, useCallback, useContext } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { ApiReducer, ApiState } from './ApiReducer';
import { AuthContext } from '../context/AuthContext';

export default () => {
    const [state, dispatch] = useReducer(ApiReducer, {
        status: 'empty',
        isLoading: false,
        error: null,
        endpoint: null,
    } as ApiState);

    const { token } = useContext(AuthContext);

    // Helper to build headers with optional auth token
    const buildHeaders = useCallback(
        (config?: AxiosRequestConfig | null): Record<string, string> => {
            return {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...config?.headers,
            };
        },
        [token]
    );

    type GetProps = {
        endpoint: string;
        queryParam?: string | null;
        config?: AxiosRequestConfig | null;
        fullUrl?: string | null;
    };

    const getApiData = useCallback(
        async (props: GetProps): Promise<any> => {
            const { endpoint, queryParam = '', config, fullUrl } = props;

            try {
                dispatch({ type: 'request_started', endpoint });

                if (!endpoint && !fullUrl) {
                    throw new Error('Missing url parameter');
                }

                const url = fullUrl || endpoint;
                const finalUrl = queryParam ? `${url}${queryParam}` : url;

                console.log('[useApi] GET request to:', finalUrl);

                const response = await axios.get(finalUrl, {
                    ...config,
                    headers: buildHeaders(config),
                });

                console.log('[useApi] GET response:', response.status, response.statusText);
                dispatch({ type: 'request_success', result: response.data, endpoint });
                return response.data;
            } catch (error: any) {
                console.error('[useApi] GET request failed:', {
                    url: fullUrl || endpoint,
                    message: error.message,
                    code: error.code,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    headers: error.config?.headers,
                });
                const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
                dispatch({ type: 'request_error', error: errorMessage, endpoint });
                throw error;
            }
        },
        [buildHeaders]
    );

    const postApiData = useCallback(
        async (props: GetProps & { data?: any }): Promise<any> => {
            const { endpoint, data, config, fullUrl } = props;

            try {
                dispatch({ type: 'request_started', endpoint });

                if (!endpoint && !fullUrl) {
                    throw new Error('Missing url parameter');
                }

                const url = fullUrl || endpoint;

                const response = await axios.post(url, data, {
                    ...config,
                    headers: buildHeaders(config),
                });

                dispatch({ type: 'request_success', result: response.data, endpoint });
                return response.data;
            } catch (error: any) {
                console.error('API POST Error:', {
                    url: fullUrl || endpoint,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    requestData: data,
                    responseData: error.response?.data,
                    headers: error.response?.headers,
                    message: error.message,
                });

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Unknown error occurred';
                dispatch({ type: 'request_error', error: errorMessage, endpoint });
                throw error;
            }
        },
        [buildHeaders]
    );

    const putApiData = useCallback(
        async (props: GetProps & { data?: any }): Promise<any> => {
            const { endpoint, data, config, fullUrl } = props;

            try {
                dispatch({ type: 'request_started', endpoint });

                if (!endpoint && !fullUrl) {
                    throw new Error('Missing url parameter');
                }

                const url = fullUrl || endpoint;

                const response = await axios.put(url, data, {
                    ...config,
                    headers: buildHeaders(config),
                });

                dispatch({ type: 'request_success', result: response.data, endpoint });
                return response.data;
            } catch (error: any) {
                console.error('API PUT Error:', {
                    url: fullUrl || endpoint,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    headers: error.response?.headers,
                    message: error.message,
                });

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Unknown error occurred';
                dispatch({ type: 'request_error', error: errorMessage, endpoint });
                throw error;
            }
        },
        [buildHeaders]
    );

    return { state, getApiData, postApiData, putApiData };
};
